export const RIBBON_AVAILABILITIES = ["off", "optional", "on"];
export const RIBBON_AVAILABILITY_LABELS = {
    off: { label: "Off", hint: "Pages of this template have no ribbon and no switch." },
    optional: { label: "Available, off by default", hint: "Each page has a switch; new pages start without the ribbon." },
    on: { label: "Available, on by default", hint: "Each page has a switch; new pages start with the ribbon." },
};
export function isRibbonAvailability(value) {
    return value === "off" || value === "optional" || value === "on";
}
/**
 * Read any stored or wire value as an availability. Never throws.
 *
 * `undefined`/`null` — a template written before the ribbon existed, or the
 * virtual Default — reads as `optional`: offering the switch commits a page to
 * nothing, and every page row starts with its ribbon off. A legacy round-1
 * object (`{ preset, opacity }`) reads as `optional` for the same reason.
 * Only an explicit `"off"` hides the switch.
 */
export function hydrateRibbonAvailability(value) {
    return isRibbonAvailability(value) ? value : "optional";
}
/** Validate a write. Absent/null/legacy object → `optional`; a wrong string is refused. */
export function validateTemplateRibbon(input) {
    if (input === undefined || input === null || (typeof input === "object" && !Array.isArray(input))) {
        return { ok: true, value: "optional" };
    }
    if (isRibbonAvailability(input))
        return { ok: true, value: input };
    return {
        ok: false,
        error: {
            field: "ribbon",
            code: "invalid",
            message: `"${String(input)}" is not one of off, optional or on`,
        },
    };
}
/** The ribbon's opacity over the page — the prototype's production value. Not editable. */
export const DEFAULT_RIBBON_OPACITY = 0.65;
export const RIBBON_OPENINGS = [
    "strand",
    "funnel",
    "product",
    "bent-left",
    "bent-right",
    "field",
    "band",
    "diagonal",
];
export const RIBBON_OPENING_LABELS = {
    strand: { name: "Narrow strand", hint: "Starts thin and centred, opens wide, then curves through chapters." },
    funnel: { name: "Funnel", hint: "Wide at the top, narrowing into a thin spine." },
    product: { name: "Leaning curve", hint: "A gentle bend that straightens as you scroll." },
    "bent-left": { name: "Leaning left", hint: "Sweeps in from the left, then settles in the centre." },
    "bent-right": { name: "Leaning right", hint: "Sweeps in from the right, then settles in the centre." },
    field: { name: "Wide field", hint: "A broad angled wash that tightens as you scroll." },
    band: { name: "Horizontal band", hint: "A soft band across the page that converges." },
    diagonal: { name: "Still diagonal", hint: "A quiet low diagonal that doesn't react to scroll." },
};
export const RIBBON_ENDINGS = ["handoff", "hold"];
export const RIBBON_ENDING_LABELS = {
    handoff: { name: "Hands off", hint: "Turns horizontal at the closing or final block and scrolls away before the footer." },
    hold: { name: "Holds", hint: "Keeps its shape to the end of the page." },
};
export function isRibbonOpening(value) {
    return typeof value === "string" && RIBBON_OPENINGS.includes(value);
}
export function isRibbonEnding(value) {
    return value === "handoff" || value === "hold";
}
/** What a version that says nothing about its ribbon reads as: off, automatic. */
export const DEFAULT_PAGE_RIBBON = {
    enabled: false,
    opening: null,
    ending: null,
};
/**
 * Read a stored ribbon row (or nothing) as settings. Never throws: an unknown
 * pin reads as automatic rather than as a page that cannot be opened.
 */
export function hydratePageRibbon(value) {
    if (typeof value !== "object" || value === null)
        return { ...DEFAULT_PAGE_RIBBON };
    const row = value;
    return {
        enabled: row.enabled === true,
        opening: isRibbonOpening(row.opening) ? row.opening : null,
        ending: isRibbonEnding(row.ending) ? row.ending : null,
    };
}
/**
 * Validate a PATCH body for a page's ribbon. The same rules on every channel:
 * `enabled` must be a boolean when present; a pin must be null or a known cue.
 */
export function validatePageRibbonChanges(input) {
    if (typeof input !== "object" || input === null || Array.isArray(input)) {
        return { ok: false, error: "Expected an object of ribbon settings" };
    }
    const body = input;
    const value = {};
    if (body.enabled !== undefined) {
        if (typeof body.enabled !== "boolean") {
            return { ok: false, error: "enabled must be true or false" };
        }
        value.enabled = body.enabled;
    }
    if (body.opening !== undefined) {
        if (body.opening !== null && !isRibbonOpening(body.opening)) {
            return { ok: false, error: `opening must be null or one of ${RIBBON_OPENINGS.join(", ")}` };
        }
        value.opening = body.opening;
    }
    if (body.ending !== undefined) {
        if (body.ending !== null && !isRibbonEnding(body.ending)) {
            return { ok: false, error: `ending must be null or one of ${RIBBON_ENDINGS.join(", ")}` };
        }
        value.ending = body.ending;
    }
    return { ok: true, value };
}
/** Apply a validated edit to stored settings. */
export function applyPageRibbonChanges(current, changes) {
    return {
        enabled: changes.enabled ?? current.enabled,
        opening: changes.opening === undefined ? current.opening : changes.opening,
        ending: changes.ending === undefined ? current.ending : changes.ending,
    };
}
export const RIBBON_ROLES = ["flood", "chapter", "endcap", "settle"];
export const RIBBON_ROLE_LABELS = {
    flood: { name: "Open up", hint: "The strand widens into its full wash here." },
    chapter: { name: "Chapter", hint: "The ribbon bends to frame this section." },
    endcap: { name: "Hand off", hint: "Fades, turns horizontal and scrolls away with this block." },
    settle: { name: "Settle", hint: "Resolves into its second shape here." },
};
/** The "no role" choice, worded like the roles. */
export const RIBBON_NONE_LABEL = {
    name: "No ribbon change",
    hint: "Overrides Auto so this block does not change the ribbon.",
};
/**
 * The reserved block scalar that overrides a block's inferred role. Absent or
 * empty means "automatic"; `none` suppresses an inferred role. It is never part
 * of a component's slot schema and the renderer ignores it.
 */
export const RIBBON_WAYPOINT_KEY = "ribbonWaypoint";
export const RIBBON_WAYPOINT_NONE = "none";
export function isRibbonRole(value) {
    return value === "flood" || value === "chapter" || value === "endcap" || value === "settle";
}
/** The block's explicit override, or null for automatic (absent, empty or unknown). */
export function ribbonWaypointOverrideOf(block) {
    const value = block.scalars?.[RIBBON_WAYPOINT_KEY] ?? "";
    if (value === RIBBON_WAYPOINT_NONE)
        return RIBBON_WAYPOINT_NONE;
    return isRibbonRole(value) ? value : null;
}
// ── Inference ───────────────────────────────────────────────────────────────
/** Hero-family blocks: what a page opens with, never a waypoint themselves. */
export const RIBBON_HERO_COMPONENTS = [
    "Hero",
    "HeroCentered",
    "TextHero",
    "HeroMedia2Column",
    "HeroMediaCentered",
];
/** Reading-only pages (policies, articles) get the still diagonal. */
export const RIBBON_QUIET_COMPONENTS = ["RichText", "Accordion", "ArticleHeader"];
/** A closing call-to-action the ribbon hands off at when it is the last block. */
export const RIBBON_CALLOUT_COMPONENTS = [
    "Callout",
    "Cta",
    "Promo",
    "Promo2Column",
    "PromoImage",
];
/**
 * Opening rules, first match wins: the block type that gives a page its
 * character decides where the ribbon starts.
 */
export const RIBBON_OPENING_RULES = [
    [["Chapters"], "strand"],
    [["Timeline"], "funnel"],
    [["Carousel"], "product"],
    [["ResourceCards"], "bent-left"],
    [["CollectionList", "NewsList", "FeaturedPress"], "bent-right"],
    [["CareersList"], "field"],
    [["ContactForm"], "band"],
];
/**
 * The block a two-shape opening settles into automatically. ContactForm picks
 * the horizontal band opening but does not converge it: the prototype Contact
 * page carries no transition marker and holds that broad opening throughout.
 * An author can still opt a block into `settle` explicitly.
 */
export const RIBBON_SIGNATURE_COMPONENTS = RIBBON_OPENING_RULES.filter(([, opening]) => opening !== "strand" && opening !== "band").flatMap(([components]) => components);
export function inferRibbonOpening(blocks) {
    const ids = new Set(blocks.map((block) => block.componentId));
    for (const [components, opening] of RIBBON_OPENING_RULES) {
        if (components.some((id) => ids.has(id)))
            return opening;
    }
    const content = blocks.filter((block) => !RIBBON_HERO_COMPONENTS.includes(block.componentId));
    if (content.length > 0 && content.every((block) => RIBBON_QUIET_COMPONENTS.includes(block.componentId))) {
        return "diagonal";
    }
    return "strand";
}
/**
 * Soften a role the opening cannot draw, or drop it.
 *  - Chapters need the strand's curved centerline; off the strand they settle.
 *  - Flood is the strand's opening; on a two-shape opening it means settle.
 *  - The strand has no second shape; a settle there floods instead.
 *  - The diagonal is still: it only ever hands off.
 *  - `hold` forbids every hand-off.
 */
export function normalizeRibbonRole(role, opening, ending) {
    if (role === "endcap")
        return ending === "hold" ? null : "endcap";
    if (opening === "diagonal")
        return null;
    if (opening === "strand")
        return role === "settle" ? "flood" : role;
    return role === "chapter" || role === "flood" ? "settle" : role;
}
/** Canonical choices an opening can draw. Endcap remains listed so Holds can disable it visibly. */
export function ribbonRoleChoices(opening) {
    if (opening === "strand")
        return ["flood", "chapter", "endcap"];
    if (opening === "diagonal")
        return ["endcap"];
    return ["settle", "endcap"];
}
/** The role a block's component implies, before overrides and normalization. */
export function inferRibbonRole(block, index, blocks, opening) {
    const id = block.componentId;
    if (id === "Chapters")
        return "chapter";
    if (RIBBON_SIGNATURE_COMPONENTS.includes(id)) {
        const first = blocks.findIndex((b) => RIBBON_SIGNATURE_COMPONENTS.includes(b.componentId));
        return first === index ? "settle" : null;
    }
    if (id === "Statement" && opening === "strand") {
        const heroIndex = blocks.findIndex((b) => RIBBON_HERO_COMPONENTS.includes(b.componentId));
        const firstStatement = blocks.findIndex((b, i) => b.componentId === "Statement" && i > heroIndex);
        return firstStatement === index ? "flood" : null;
    }
    if (RIBBON_CALLOUT_COMPONENTS.includes(id) && index === blocks.length - 1)
        return "endcap";
    return null;
}
/** Resolve a page's ribbon from its blocks and its pinned settings. */
export function planRibbon(blocks, settings = {}) {
    const ordered = [...blocks].sort((a, b) => a.position - b.position);
    const opening = settings.opening ?? inferRibbonOpening(ordered);
    // Auto must be allowed to discover an endcap before its resolved visual
    // ending is known. An explicit Hold is the only setting that suppresses one.
    const roleEnding = settings.ending === "hold" ? "hold" : "handoff";
    // A ribbon opens once and settles once. When an author marks a block for one
    // of those, their choice wins over anything the blocks would have implied —
    // an inferred flood higher up must not swallow the flood they asked for.
    const explicitRoles = new Set();
    ordered.forEach((block) => {
        const override = ribbonWaypointOverrideOf(block);
        if (override && override !== RIBBON_WAYPOINT_NONE) {
            const role = normalizeRibbonRole(override, opening, roleEnding);
            if (role)
                explicitRoles.add(role);
        }
    });
    const waypoints = [];
    let flooded = false;
    let settled = false;
    ordered.forEach((block, index) => {
        const override = ribbonWaypointOverrideOf(block);
        if (override === RIBBON_WAYPOINT_NONE)
            return;
        const raw = override ?? inferRibbonRole(block, index, ordered, opening);
        if (!raw)
            return;
        const role = normalizeRibbonRole(raw, opening, roleEnding);
        if (!role)
            return;
        const inferred = override === null;
        if (role === "flood") {
            if (flooded || (inferred && explicitRoles.has("flood")))
                return;
            flooded = true;
        }
        if (role === "settle") {
            if (settled || (inferred && explicitRoles.has("settle")))
                return;
            settled = true;
        }
        waypoints.push({
            blockId: block.id,
            componentId: block.componentId,
            role,
            source: inferred ? "component-auto" : "block-override",
            inferred,
        });
    });
    // Hand-off is a one-shot transition. Precedence matters before position:
    // an author's block override must not be displaced by a later automatic
    // Promo/Callout. Within the same provenance, the final request wins.
    const endcapPriority = {
        "component-auto": 0,
        "block-override": 1,
        "page-ending": 2,
    };
    let winningEndcap = -1;
    let winningPriority = -1;
    waypoints.forEach((waypoint, index) => {
        if (waypoint.role !== "endcap")
            return;
        const priority = endcapPriority[waypoint.source];
        if (priority >= winningPriority) {
            winningEndcap = index;
            winningPriority = priority;
        }
    });
    if (winningEndcap >= 0) {
        for (let index = waypoints.length - 1; index >= 0; index -= 1) {
            if (waypoints[index].role === "endcap" && index !== winningEndcap)
                waypoints.splice(index, 1);
        }
    }
    let hasEndcap = waypoints.some((waypoint) => waypoint.role === "endcap");
    if (settings.ending === "handoff" && !hasEndcap && ordered.length > 0) {
        // A page pin outranks block inference/overrides. Prefer the last semantic
        // closing block; otherwise synthesize the release on the final block.
        const target = [...ordered].reverse().find((block) => RIBBON_CALLOUT_COMPONENTS.includes(block.componentId)) ?? ordered[ordered.length - 1];
        waypoints.push({
            blockId: target.id,
            componentId: target.componentId,
            role: "endcap",
            source: "page-ending",
            inferred: false,
        });
        hasEndcap = true;
    }
    // Keep the contract's waypoint order independent of the order in which a
    // page-level fallback was synthesized. A hand-off is terminal: once the
    // ribbon releases with that block, later content must not steer a stale
    // scene back into a flood, settle, or chapter shape.
    const blockOrder = new Map(ordered.map((block, index) => [block.id, index]));
    waypoints.sort((left, right) => (blockOrder.get(left.blockId) ?? Number.MAX_SAFE_INTEGER) -
        (blockOrder.get(right.blockId) ?? Number.MAX_SAFE_INTEGER));
    const endcapIndex = waypoints.findIndex((waypoint) => waypoint.role === "endcap");
    if (endcapIndex >= 0)
        waypoints.splice(endcapIndex + 1);
    const ending = settings.ending === "hold"
        ? "hold"
        : hasEndcap
            ? "handoff"
            : "hold";
    return { opening, ending, waypoints };
}
//# sourceMappingURL=ribbon.js.map